import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, m } from 'motion/react';
import { CheckCircle2, Info, XCircle } from 'lucide-react';
import { useToastStore, type Toast } from '@/stores/toast';

const EASE = [0.16, 1, 0.3, 1] as const;

const TONE = {
  info: { icon: Info, className: 'text-primary' },
  success: { icon: CheckCircle2, className: 'text-income' },
  error: { icon: XCircle, className: 'text-expense' },
} as const;

function ToastItem({ toast }: { toast: Toast }) {
  const dismiss = useToastStore((s) => s.dismiss);
  const { icon: Icon, className } = TONE[toast.tone];

  useEffect(() => {
    const t = window.setTimeout(() => dismiss(toast.id), 3000);
    return () => window.clearTimeout(t);
  }, [toast.id, dismiss]);

  return (
    <m.div
      initial={{ opacity: 0, y: 12, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.98 }}
      transition={{ duration: 0.2, ease: EASE }}
      role="status"
      className="pointer-events-auto flex items-center gap-2.5 rounded-lg bg-surface px-4 py-3 shadow-hover"
    >
      <Icon className={`size-5 shrink-0 ${className}`} />
      <span className="text-sm font-medium text-ink">{toast.message}</span>
    </m.div>
  );
}

/** Fila de toasts, ancorada no rodapé central. Montada uma vez no AppShell. */
export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);

  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 bottom-20 z-[60] flex flex-col items-center gap-2 px-4 lg:bottom-6">
      <AnimatePresence>
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} />
        ))}
      </AnimatePresence>
    </div>,
    document.body,
  );
}
